import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Heart, ArrowLeft, X, Check, Search, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayName, formatPronouns } from '@/lib/displayName';
import { badgeStyle } from '@/lib/badgeStyle';

const NAVY = '#1D2D44';
const GOLD = '#C9A84C';
const INK = '#0F1A2A';
const PAPER = '#16243D';
const MUTED = '#8FA0BD';
const LINE = 'rgba(201,168,76,0.22)';
const TEXT = '#F5F1E6';

const StatusBadge = ({ status }) => {
  const map = {
    complete: { label: 'Complete', bg: 'rgba(94,127,99,0.25)', color: '#A3D9A5' },
    in_progress: { label: 'In progress', bg: 'rgba(201,168,76,0.18)', color: GOLD },
    not_started: { label: 'Not started', bg: 'rgba(255,255,255,0.06)', color: MUTED },
  };
  const s = map[status] || map.not_started;
  return (
    <span
      className="inline-block text-[10px] tracking-widest uppercase px-2 py-1 rounded-full"
      style={{ backgroundColor: s.bg, color: s.color }}
      data-testid={`map-status-${status}`}
    >
      {s.label}
    </span>
  );
};

const FoundingPill = ({ paid }) => (
  <span
    className="inline-block text-[10px] tracking-widest uppercase px-2 py-1 rounded-full"
    style={{
      backgroundColor: paid ? GOLD : 'rgba(255,255,255,0.06)',
      color: paid ? NAVY : MUTED,
      fontWeight: paid ? 600 : 400,
    }}
  >
    {paid ? 'Paid' : 'Not paid'}
  </span>
);

const StatCard = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl p-5"
    style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
  >
    <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
      {label}
    </p>
    <p className="font-serif text-3xl" style={{ color: TEXT }}>
      {value ?? '—'}
    </p>
  </div>
);

const AdminPhoto = ({ userId, photoId, axiosAuth, alt }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let obj = null;
    (async () => {
      try {
        const api = axiosAuth();
        const r = await api.get(`/admin/members/${userId}/photos/${photoId}`, {
          responseType: 'blob',
          params: { auth: localStorage.getItem('token') },
        });
        if (revoked) return;
        obj = URL.createObjectURL(r.data);
        setUrl(obj);
      } catch (e) {
        console.error('admin photo fetch', e);
      }
    })();
    return () => {
      revoked = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [userId, photoId, axiosAuth]);

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ color: MUTED }}>
        …
      </div>
    );
  }
  return <img src={url} alt={alt} className="w-full h-full object-cover" />;
};

const MemberDetail = ({ userId, axiosAuth, onClose, onUpdated }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const r = await api.get(`/admin/members/${userId}`);
      setDetail(r.data);
      setNotes(r.data.admin_notes || '');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load member.');
    } finally {
      setLoading(false);
    }
  }, [userId, axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const toggleFounding = async () => {
    try {
      const api = axiosAuth();
      const next = !detail.is_founding_member;
      await api.post(`/admin/members/${userId}/founding`, { is_founding_member: next });
      setDetail((d) => ({ ...d, is_founding_member: next }));
      toast.success(next ? 'Marked as paid.' : 'Marked as not paid.');
      onUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error('Could not update founding status.');
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const api = axiosAuth();
      await api.post(`/admin/members/${userId}/notes`, { notes });
      toast.success('Notes saved.');
    } catch (e) {
      console.error(e);
      toast.error('Could not save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
      data-testid="member-detail-overlay"
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto"
        style={{ backgroundColor: INK, borderLeft: `1px solid ${LINE}` }}
        onClick={(e) => e.stopPropagation()}
        data-testid="member-detail-panel"
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 flex items-center justify-between"
          style={{ backgroundColor: 'rgba(15,26,42,0.95)', borderBottom: `1px solid ${LINE}` }}
        >
          <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>
            Member Detail
          </p>
          <button
            onClick={onClose}
            data-testid="close-detail-btn"
            className="rounded-full p-2 transition hover:bg-white/5"
            style={{ color: MUTED }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="p-12 text-center" style={{ color: MUTED }}>Loading…</div>
        )}

        {!loading && detail && (
          <div className="px-6 py-6 space-y-6">
            {/* Identity */}
            <div>
              <h2 className="font-serif text-3xl mb-1" style={{ color: TEXT }}>
                {formatDisplayName(
                  detail.profile?.first_name || detail.name,
                  detail.profile?.last_name_initial
                )}
              </h2>
              {formatPronouns(detail.profile?.pronouns) && (
                <p className="text-xs mb-1" style={{ color: MUTED }} data-testid="detail-pronouns">
                  {formatPronouns(detail.profile?.pronouns)}
                </p>
              )}
              <p className="text-sm" style={{ color: MUTED }}>{detail.email}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <StatusBadge status={detail.map_status} />
                <FoundingPill paid={detail.is_founding_member} />
                {detail.is_admin && (
                  <span
                    className="inline-block text-[10px] tracking-widest uppercase px-2 py-1 rounded-full"
                    style={{ backgroundColor: NAVY, color: GOLD, border: `1px solid ${GOLD}` }}
                  >
                    Admin
                  </span>
                )}
              </div>
            </div>

            {/* Quick facts */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl p-3" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>City</p>
                <p style={{ color: TEXT }}>{detail.profile?.city || '—'}</p>
              </div>
              <div className="rounded-xl p-3" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>Age</p>
                <p style={{ color: TEXT }}>{detail.profile?.age ?? '—'}</p>
              </div>
              <div className="rounded-xl p-3" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>Joined</p>
                <p style={{ color: TEXT }}>{detail.joined_at ? new Date(detail.joined_at).toLocaleDateString() : '—'}</p>
              </div>
              <div className="rounded-xl p-3" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>Map</p>
                <p style={{ color: TEXT }}>{detail.completed_checkpoints}/{detail.total_checkpoints}</p>
              </div>
            </div>

            {/* Admin Actions */}
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
            >
              <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>
                Admin Actions
              </p>
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <p className="text-sm" style={{ color: TEXT }}>Founding Member</p>
                  <p className="text-xs" style={{ color: MUTED }}>
                    Toggle to mark this member as paid (manual PayPal).
                  </p>
                </div>
                <Button
                  data-testid="toggle-founding-btn"
                  onClick={toggleFounding}
                  className="rounded-full"
                  style={{
                    backgroundColor: detail.is_founding_member ? GOLD : 'transparent',
                    color: detail.is_founding_member ? NAVY : TEXT,
                    border: `1px solid ${GOLD}`,
                    fontWeight: 600,
                  }}
                >
                  {detail.is_founding_member ? (
                    <><Check size={14} className="mr-1" /> Paid</>
                  ) : 'Mark as Paid'}
                </Button>
              </div>

              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: MUTED }}>
                Private Admin Notes
              </p>
              <Textarea
                data-testid="admin-notes-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={5000}
                rows={4}
                placeholder='e.g., "Matched with Sara on Feb 1, 2026"'
                className="rounded-lg border-0"
                style={{ backgroundColor: INK, color: TEXT, border: `1px solid ${LINE}` }}
              />
              <div className="flex justify-end mt-3">
                <Button
                  data-testid="save-notes-btn"
                  onClick={saveNotes}
                  disabled={savingNotes}
                  className="rounded-full text-sm"
                  style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}
                >
                  {savingNotes ? 'Saving…' : 'Save Notes'}
                </Button>
              </div>
            </div>

            {/* Photos */}
            {detail.photos && detail.photos.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>
                  Profile Photos
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {detail.photos.map((p) => (
                    <div
                      key={p.id}
                      className="aspect-square rounded-xl overflow-hidden"
                      style={{ border: `1px solid ${LINE}`, backgroundColor: INK }}
                    >
                      <AdminPhoto
                        userId={detail.user_id}
                        photoId={p.id}
                        axiosAuth={axiosAuth}
                        alt={`Photo ${p.order + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Profile */}
            {detail.profile && Object.keys(detail.profile).length > 0 && (
              <div
                className="rounded-2xl p-5 space-y-4"
                style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
              >
                <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>
                  Profile
                </p>

                {detail.profile.my_energy && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>My Energy</p>
                    <p className="text-sm" style={{ color: TEXT }}>{detail.profile.my_energy}</p>
                  </div>
                )}

                {detail.profile.relationship_rhythm && Object.keys(detail.profile.relationship_rhythm).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>Relationship Rhythm</p>
                    <ul className="text-sm space-y-1" style={{ color: TEXT }}>
                      {Object.entries(detail.profile.relationship_rhythm)
                        .filter(([k]) => k !== 'love_language_awareness')
                        .map(([k, v]) => (
                        <li key={k}><span style={{ color: MUTED }}>{k.replace(/_/g, ' ')}:</span> {v}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {detail.profile.how_i_love && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: MUTED }}>How I Love</p>
                    <p className="text-sm" style={{ color: TEXT }}>{detail.profile.how_i_love}</p>
                  </div>
                )}

                {detail.profile.intentionality_badges && detail.profile.intentionality_badges.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: MUTED }}>Intentionality Badges</p>
                    <div className="flex flex-wrap gap-2">
                      {detail.profile.intentionality_badges.map((b) => (
                        <span
                          key={b}
                          data-testid={`detail-badge-${b.replace(/\s+/g, '-').toLowerCase()}`}
                          className="text-xs px-3 py-1 rounded-full"
                          style={badgeStyle(b, true)}
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(detail.profile.relationship_rhythm?.love_language_awareness
                  || detail.profile.ll_reflection_1
                  || detail.profile.ll_reflection_2
                  || detail.profile.ll_reflection_3
                  || detail.profile.ll_suggested_pattern) && (
                  <div data-testid="detail-love-language-section">
                    <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: MUTED }}>
                      Love Language &amp; Emotional Style
                    </p>
                    {detail.profile.relationship_rhythm?.love_language_awareness && (
                      <p className="text-sm mb-2" style={{ color: TEXT }}>
                        <span style={{ color: MUTED }}>Awareness:</span>{' '}
                        {detail.profile.relationship_rhythm.love_language_awareness === 'known'
                          ? 'Known'
                          : 'Exploring'}
                      </p>
                    )}
                    {[1, 2, 3].map((i) => {
                      const v = detail.profile[`ll_reflection_${i}`];
                      const labels = {
                        1: 'I feel most appreciated when someone…',
                        2: 'I feel disconnected in a relationship when…',
                        3: 'I naturally show love by…',
                      };
                      if (!v) return null;
                      return (
                        <div
                          key={i}
                          data-testid={`detail-ll-reflection-${i}`}
                          className="rounded-lg p-3 mb-2"
                          style={{ backgroundColor: INK, border: `1px solid ${LINE}` }}
                        >
                          <p className="font-serif italic text-xs mb-1" style={{ color: GOLD }}>
                            {labels[i]}
                          </p>
                          <p className="text-sm" style={{ color: TEXT }}>{v}</p>
                        </div>
                      );
                    })}
                    {detail.profile.ll_suggested_pattern && (
                      <div
                        data-testid="detail-ll-suggested-pattern"
                        className="rounded-lg p-3 mt-2"
                        style={{
                          backgroundColor: 'rgba(94,127,99,0.10)',
                          border: '1px solid rgba(94,127,99,0.30)',
                        }}
                      >
                        <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: '#A3D9A5' }}>
                          Possible pattern (optional insight)
                        </p>
                        <p className="text-sm" style={{ color: TEXT }}>
                          {detail.profile.ll_suggested_pattern}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {detail.profile.guided_prompts && detail.profile.guided_prompts.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: MUTED }}>Guided Prompts</p>
                    <div className="space-y-3">
                      {detail.profile.guided_prompts.map((p, i) => (
                        <div key={i} className="rounded-lg p-3" style={{ backgroundColor: INK, border: `1px solid ${LINE}` }}>
                          <p className="font-serif italic text-sm mb-1" style={{ color: GOLD }}>{p.prompt}</p>
                          <p className="text-sm" style={{ color: TEXT }}>{p.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Relationship Map answers */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>
                Relationship Map — All Reflections
              </p>
              {detail.reflections.length === 0 && (
                <p className="text-sm" style={{ color: MUTED }}>No reflections yet.</p>
              )}
              <div className="space-y-3">
                {detail.reflections.map((r) => (
                  <div
                    key={r.checkpoint_id}
                    data-testid={`reflection-${r.checkpoint_id}`}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
                  >
                    <p className="font-serif text-lg mb-1" style={{ color: TEXT }}>
                      {r.order}. {r.checkpoint_name}
                    </p>
                    <p className="text-xs mb-3" style={{ color: MUTED }}>{r.checkpoint_subtitle}</p>

                    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: GOLD }}>
                      Serious
                    </p>
                    <p className="text-sm italic mb-2" style={{ color: MUTED }}>
                      {r.serious_question}
                    </p>
                    <p className="text-sm font-serif whitespace-pre-wrap mb-3" style={{ color: TEXT }}>
                      {r.serious_answer}
                    </p>

                    {r.lighter_answer && (
                      <>
                        <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: GOLD }}>
                          Lighter
                        </p>
                        <p className="text-sm italic mb-2" style={{ color: MUTED }}>
                          {r.lighter_question}
                        </p>
                        <p className="text-sm whitespace-pre-wrap" style={{ color: TEXT }}>
                          {r.lighter_answer}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const [s, m] = await Promise.all([api.get('/admin/stats'), api.get('/admin/members')]);
      setStats(s.data);
      setMembers(m.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 403) {
        toast.error('Not authorized.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data.');
      }
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = members.filter((m) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (m.email || '').toLowerCase().includes(q) ||
      (m.name || '').toLowerCase().includes(q) ||
      (m.first_name || '').toLowerCase().includes(q) ||
      (m.city || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      <header
        className="sticky top-0 z-40 backdrop-blur"
        style={{ backgroundColor: 'rgba(15,26,42,0.9)', borderBottom: `1px solid ${LINE}` }}
      >
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center text-sm" style={{ color: MUTED }}
            data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Back to Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={16} style={{ color: GOLD }} />
            <span className="font-serif text-lg" style={{ color: TEXT }}>
              Admin · Our Love <Heart className="inline-block w-4 h-4 mx-1" style={{ color: GOLD }} fill={GOLD} strokeWidth={1.5} /> Connection
            </span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-dashboard">
        <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] mb-1" style={{ color: GOLD }}>
              Founder Console
            </p>
            <h1 className="font-serif text-4xl" style={{ color: TEXT }}>Members</h1>
          </div>
          <Link to="/admin/compare">
            <Button
              data-testid="open-compare-btn"
              className="rounded-full"
              style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}
            >
              <Shield size={14} className="mr-2" />
              Compare Members
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
          <StatCard label="Total Members" value={stats?.total_members} testid="stat-total" />
          <StatCard label="Map Completed" value={stats?.map_completed} testid="stat-map-complete" />
          <StatCard label="Profiles Built" value={stats?.profiles_completed} testid="stat-profiles" />
          <StatCard label="Founding (Paid)" value={stats?.founding_members} testid="stat-founding" />
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
          <Input
            data-testid="member-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or city…"
            className="rounded-full pl-9 border-0"
            style={{ backgroundColor: PAPER, color: TEXT, border: `1px solid ${LINE}` }}
          />
        </div>

        {/* Members Table */}
        {loading && (
          <div className="py-16 text-center" style={{ color: MUTED }}>Loading members…</div>
        )}

        {!loading && (
          <div
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                    {['Name', 'Email', 'City', 'Age', 'Joined', 'Map', 'Founding', ''].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-[10px] uppercase tracking-widest"
                        style={{ color: MUTED, fontWeight: 600 }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr
                      key={m.user_id}
                      data-testid={`member-row-${m.email}`}
                      className="transition-colors hover:bg-white/[0.03]"
                      style={{ borderBottom: `1px solid rgba(201,168,76,0.08)` }}
                    >
                      <td className="px-4 py-3" style={{ color: TEXT }}>
                        {formatDisplayName(m.first_name || m.name, m.last_name_initial)}
                        {formatPronouns(m.pronouns) && (
                          <span className="ml-2 text-xs" style={{ color: MUTED }} data-testid={`row-pronouns-${m.email}`}>
                            {formatPronouns(m.pronouns)}
                          </span>
                        )}
                        {m.is_admin && (
                          <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: NAVY, color: GOLD, border: `1px solid ${GOLD}` }}>
                            Admin
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: MUTED }}>{m.email}</td>
                      <td className="px-4 py-3" style={{ color: MUTED }}>{m.city || '—'}</td>
                      <td className="px-4 py-3" style={{ color: MUTED }}>{m.age ?? '—'}</td>
                      <td className="px-4 py-3" style={{ color: MUTED }}>
                        {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={m.map_status} /></td>
                      <td className="px-4 py-3"><FoundingPill paid={m.is_founding_member} /></td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          data-testid={`view-details-btn-${m.email}`}
                          onClick={() => setSelectedUserId(m.user_id)}
                          variant="outline"
                          className="rounded-full text-xs px-3 py-1 h-auto"
                          style={{
                            backgroundColor: 'transparent',
                            color: GOLD,
                            border: `1px solid ${GOLD}`,
                          }}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center" style={{ color: MUTED }}>
                        No members match your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {selectedUserId && (
        <MemberDetail
          userId={selectedUserId}
          axiosAuth={axiosAuth}
          onClose={() => setSelectedUserId(null)}
          onUpdated={loadAll}
        />
      )}
    </div>
  );
};

export default AdminPage;
